import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Stack, Text, Textarea,
} from "@chakra-ui/react";
import { useWith2FA } from "tools/twoFactor";
import {
    buildInitialEmailForm, validateEmailForm, buildEmailPayload,
    buildUserEmailConfig, buildGroupEmailConfig,
} from "./adminEmailApi";

/**
 * Builds the axios config for the given target (user or group).
 * @param {string} targetType - "user" or "group"
 * @param {number|string} targetId - Recipient id
 * @param {Object} payload - Result of buildEmailPayload
 * @returns {Object} Axios request config
 */
function buildConfig(targetType, targetId, payload) {
    return targetType === "group"
        ? buildGroupEmailConfig(targetId, payload)
        : buildUserEmailConfig(targetId, payload);
}

/**
 * Validates, then sends the email through the shared 2FA flow. Server errors
 * (400/404) are surfaced verbatim; a cancelled 2FA challenge is silent.
 * @param {Object} args - { targetType, targetId, form, runWith2FA, setLoading, onClose }
 * @returns {Promise<void>}
 */
async function submitEmail({ targetType, targetId, form, runWith2FA, setLoading, onClose }) {
    const err = validateEmailForm(form);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        await runWith2FA(buildConfig(targetType, targetId, buildEmailPayload(form)));
        toast.success("Email mis en file d'envoi");
        onClose();
    } catch (error) {
        if (error?.message !== "2fa_cancelled") {
            toast.error(typeof error?.response?.data === "string" ? error.response.data : "Échec de l'envoi");
        }
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the email form fields (required message + optional subject/link/label).
 * @param {Object} props - { form, onChange }
 * @returns {React.ReactElement}
 */
function EmailFormFields({ form, onChange }) {
    return (
        <Stack spacing="14px">
            <FormControl isRequired>
                <FormLabel fontSize="sm">Message</FormLabel>
                <Textarea value={form.message} onChange={(e) => onChange("message", e.target.value)}
                    rows={6} borderRadius="12px" placeholder="Texte de l'email (multi-ligne autorisé)" />
            </FormControl>
            <FormControl>
                <FormLabel fontSize="sm">Objet (optionnel)</FormLabel>
                <Input value={form.subject} onChange={(e) => onChange("subject", e.target.value)} borderRadius="12px" />
            </FormControl>
            <FormControl>
                <FormLabel fontSize="sm">Lien (optionnel)</FormLabel>
                <Input value={form.link} onChange={(e) => onChange("link", e.target.value)}
                    borderRadius="12px" placeholder="https://…" />
            </FormControl>
            <FormControl>
                <FormLabel fontSize="sm">Libellé du bouton (optionnel)</FormLabel>
                <Input value={form.link_label} onChange={(e) => onChange("link_label", e.target.value)} borderRadius="12px" />
            </FormControl>
        </Stack>
    );
}

/**
 * Dialog letting an admin compose and send a custom email to a user or to all
 * active members of a group. Sending is protected by the shared 2FA flow.
 * @param {Object} props - { open, onClose, targetType, targetId, recipientLabel }
 * @returns {React.ReactElement|null}
 */
function SendEmailDialog({ open, onClose, targetType, targetId, recipientLabel }) {
    const runWith2FA = useWith2FA();
    const [form, setForm] = useState(buildInitialEmailForm());
    const [loading, setLoading] = useState(false);
    useEffect(() => { if (open) setForm(buildInitialEmailForm()); }, [open]);
    if (targetId === null || targetId === undefined) return null;

    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submitEmail({ targetType, targetId, form, runWith2FA, setLoading, onClose });
    const title = targetType === "group" ? "Envoyer un email au groupe" : "Envoyer un email";
    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{title}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {recipientLabel && (
                        <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "secondaryGray.400" }} mb="14px">
                            Destinataire : {recipientLabel}
                        </Text>
                    )}
                    <EmailFormFields form={form} onChange={onChange} />
                </ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading}
                        loadingText="Envoi..." isDisabled={loading || !form.message.trim()}>Envoyer</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default SendEmailDialog;
